import Pagination from 'react-bootstrap/Pagination';
import { memo, FC, useEffect, useMemo, useCallback } from "react";

import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';



interface PaginationProps {
    pagesNumber: number;
    action: (page: number) => { type: string; payload: number }; // Redux action creator
    currentPage: number;
    disabled?: boolean;
}


const MyPagination: FC<PaginationProps> = ({ pagesNumber, currentPage,
    action, disabled = false }) => {
    const dispatch = useDispatch();


    const { t } = useTranslation("components");

    const pageClickHandle = useCallback((pageNumber: number) => {
        dispatch(action(pageNumber));
    }, [action, dispatch])

    const items = useMemo(() => {
        const result = [];
        // Always show first page
        if (currentPage > 4) {
            result.push(
                <Pagination.Item key={1} active={1 === currentPage} onClick={() => pageClickHandle(1)}>
                    1
                </Pagination.Item>
            );
            if (currentPage > 5) {
                result.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        // Middle pages around current page
        for (let i = Math.max(1, currentPage - 3); i <= Math.min(pagesNumber, currentPage + 3); i++) {
            result.push(
                <Pagination.Item key={i} active={i === currentPage} onClick={() => pageClickHandle(i)}>
                    {i}
                </Pagination.Item>
            );
        }

        // Always show last page
        if (currentPage < pagesNumber - 3) {
            if (currentPage < pagesNumber - 4) {
                result.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            result.push(
                <Pagination.Item key={pagesNumber} active={pagesNumber === currentPage} onClick={() => pageClickHandle(pagesNumber)}>
                    {pagesNumber}
                </Pagination.Item>
            );
        }

        return result;
    }, [pagesNumber, currentPage, pageClickHandle]);

    useEffect(() => {
        // Wait for next frame to ensure DOM has updated
        const timeout = setTimeout(() => {
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }, 50);
    
        return () => clearTimeout(timeout);
    }, [currentPage]);
    


    if (disabled) return null;

    return (
        <Pagination style={disabled ? { display: "none" } : undefined} className={styles.pagination}>
            {currentPage > 1 && <>
                <Pagination.First title={t("pagination.first_page")} className={styles.pagination__first} disabled={currentPage === 1} onClick={() => pageClickHandle(1)} />
                <Pagination.Prev title={t("pagination.previous")} className={styles.pagination__prev} disabled={currentPage === 1} onClick={() => pageClickHandle(currentPage - 1)} />
            </>}
            {items}
            {pagesNumber !== currentPage && <>
                <Pagination.Next title={t("pagination.next_page")} disabled={currentPage === pagesNumber} onClick={() => pageClickHandle(currentPage + 1)} />
                <Pagination.Last title={t("pagination.last_page")} disabled={currentPage === pagesNumber} onClick={() => pageClickHandle(pagesNumber)} />
            </>}

        </Pagination>
    );
};



export default memo(MyPagination);